import { useState } from "react";
import { Check, Copy, Mail, MessageCircle, QrCode, Send, Share2 } from "lucide-react";
import { QrCodeModal } from "./QrCodeModal";

type Props = {
  url: string;
  emailSubject?: string;    // Betreff für mailto:
  compact?: boolean;        // Kleinere Icon-only-Variante
  qrFilename?: string;      // Dateiname für den QR-Download (ohne Endung)
};

/**
 * Teil-Buttons für einen Empfehlungs-Link.
 *
 * Geteilt wird IMMER ausschließlich die nackte URL — kein Begleittext.
 * (Bewusste Entscheidung: der Empfänger soll nur den Link sehen, nichts
 * was nach Marketing-Vorlage aussieht.)
 *
 * - Primärer "Teilen"-Button nutzt Native Web Share API (öffnet OS-Share-Sheet
 *   auf Mobile, dort sind Insta/Signal/Snap etc. dabei). Auf Desktop ohne
 *   Web-Share-Support fällt der Button weg.
 * - WhatsApp-Link: wa.me/?text=<url>
 * - Telegram-Link: t.me/share/url?url=<url>
 * - mailto:-Link mit Betreff + URL als Body
 * - Copy-Button mit visuellem Feedback
 */
export function ShareButtons({
  url,
  emailSubject = "Empfehlung",
  compact = false,
  qrFilename,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const waUrl = `https://wa.me/?text=${encodeURIComponent(url)}`;
  const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(url)}`;
  const nativeShareAvailable =
    typeof navigator !== "undefined" && "share" in navigator;

  async function nativeShare() {
    if (!nativeShareAvailable) return;
    try {
      await (navigator as Navigator & { share: (data: ShareData) => Promise<void> }).share({
        url,
      });
    } catch {
      // User canceled — kein Toast
    }
  }

  function copy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  const buttonClass = compact
    ? "inline-flex items-center justify-center rounded-lg size-9 transition"
    : "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition";

  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? "" : "sm:gap-2"}`}>
      {nativeShareAvailable && (
        <button
          type="button"
          onClick={nativeShare}
          className={`${buttonClass} brand-gradient text-white hover:brightness-110`}
          title="Über installierte App teilen"
          aria-label="Teilen"
        >
          <Share2 className="size-4" />
          {!compact && <span>Teilen</span>}
        </button>
      )}

      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${buttonClass} bg-[#25D366] text-white hover:brightness-110`}
        title="Per WhatsApp teilen"
        aria-label="Per WhatsApp teilen"
      >
        <MessageCircle className="size-4" />
        {!compact && <span>WhatsApp</span>}
      </a>

      <a
        href={tgUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${buttonClass} bg-[#229ED9] text-white hover:brightness-110`}
        title="Per Telegram teilen"
        aria-label="Per Telegram teilen"
      >
        <Send className="size-4" />
        {!compact && <span>Telegram</span>}
      </a>

      <a
        href={mailtoUrl}
        className={`${buttonClass} bg-slate-500 text-white hover:bg-slate-600`}
        title="Per Email teilen"
        aria-label="Per Email teilen"
      >
        <Mail className="size-4" />
        {!compact && <span>Email</span>}
      </a>

      <button
        type="button"
        onClick={copy}
        className={`${buttonClass} border border-line bg-white text-ink-soft hover:bg-cream-soft`}
        title="Link kopieren"
        aria-label="Link kopieren"
      >
        {copied ? <Check className="size-4 text-emerald-700" /> : <Copy className="size-4" />}
        {!compact && <span>{copied ? "Kopiert!" : "Kopieren"}</span>}
      </button>

      <button
        type="button"
        onClick={() => setQrOpen(true)}
        className={`${buttonClass} border border-line bg-white text-ink-soft hover:bg-cream-soft`}
        title="QR-Code erzeugen"
        aria-label="QR-Code erzeugen"
      >
        <QrCode className="size-4" />
        {!compact && <span>QR-Code</span>}
      </button>

      {qrOpen && (
        <QrCodeModal
          url={url}
          filename={qrFilename || "dp-empfehlungslink"}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  );
}
